import { Fragment, type ReactNode, useState } from "react";
import type { DetailRow, IntentActionType, PayloadRecord } from "./types";
import {
  getFieldLabel,
  isRecord,
  isRecordArray,
  isStringArray,
  safeParseJson,
  toDisplayValue,
} from "./utils";

export type IntentDetailUiText = {
  noDetailsProvided: string;
  sections: {
    target: string;
    fieldsToApply: string;
    payload: string;
    sortTarget: string;
    newOrder: string;
    serviceUpdates: string;
    targetScope: string;
    bulkChanges: string;
    copyScope: string;
    copyModifiers: string;
    restrictionPlan: string;
    newServices: string;
    newCategories: string;
    operatingHours: string;
    assignment: string;
    optionGroups?: string;
  };
  table: {
    newOrder: string;
    itemId: string;
    index: string;
    name: string;
    serviceCode: string;
    categoryId: string;
    basePrice: string;
    duration: string;
    description: string;
    color: string;
    onlineBooking: string;
  };
  labels: {
    serviceIds: string;
    categoryIds: string;
    closed: string;
    unassigned: string;
    discountApplyToTotal: string;
    discountApplyToPerItem: string;
    serviceItemTitle: (index: number) => string;
    serviceFallback: string;
    workerFallback: string;
  };
};

const formatDiscountApplyTo = (raw: unknown, uiText: IntentDetailUiText): string => {
  if (raw === "total") return uiText.labels.discountApplyToTotal;
  if (raw === "per_item") return uiText.labels.discountApplyToPerItem;
  return toDisplayValue(raw, "applyTo");
};

const asFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const stripTrailingZeros = (value: string): string => value.replace(/\.00$/, "");

const formatSigned = (raw: string): string => {
  if (!raw || raw === "-") return "-";
  const isNegative = raw.startsWith("-");
  const magnitude = raw.replace(/^[-+]/, "");
  const normalized = stripTrailingZeros(magnitude);
  return isNegative ? `-${normalized}` : `+${normalized}`;
};

const formatPriceAdjustmentValue = (
  rawPriceAdjustment: unknown,
  currencySymbol: string
): string => {
  if (!isRecord(rawPriceAdjustment)) {
    return toDisplayValue(rawPriceAdjustment, "priceAdjustment");
  }

  const type = typeof rawPriceAdjustment.type === "string" ? rawPriceAdjustment.type : undefined;
  const value = rawPriceAdjustment.value;

  if (type === "fixed") {
    return `${formatSigned(toDisplayValue(value, "basePrice"))}${currencySymbol}`;
  }

  if (type === "percentage" || type === "percent") {
    const n = asFiniteNumber(value);
    if (n === null) {
      const raw = String(value ?? "-");
      const alreadyHasPercent = raw.includes("%");
      return alreadyHasPercent ? raw : `${raw}%`;
    }

    const magnitude = Math.abs(n);
    const magnitudeText = Number.isInteger(magnitude)
      ? String(magnitude)
      : stripTrailingZeros(magnitude.toFixed(2));
    const signed = n < 0 ? `-${magnitudeText}` : `+${magnitudeText}`;
    return `${signed}%`;
  }

  return toDisplayValue(rawPriceAdjustment, "priceAdjustment");
};

const getWeekdayName = (dayOfWeek: number, locale?: string): string => {
  // Backend convention: 0=Sunday ... 6=Saturday
  // Use a known Sunday as anchor (2024-01-07 is Sunday).
  const baseUtc = Date.UTC(2024, 0, 7);
  const date = new Date(baseUtc + dayOfWeek * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
};

const formatCentsAsCurrency = (rawCents: unknown, currencySymbol: string): string => {
  const cents = asFiniteNumber(rawCents);
  if (cents === null) return toDisplayValue(rawCents, "minSpent");
  const amount = cents / 100;
  const text = stripTrailingZeros(amount.toFixed(2));
  return `${text}${currencySymbol}`;
};

const weekdayToIndex: Record<string, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const formatOffPeakHoursList = (raw: unknown, locale?: string): string => {
  if (!isRecordArray(raw) || raw.length === 0) return "-";

  const lines = raw
    .map((item) => {
      if (!isRecord(item)) return null;

      const dayOfWeek = typeof item.dayOfWeek === "string" ? item.dayOfWeek : "";
      const timeRanges = Array.isArray(item.timeRanges) ? item.timeRanges : [];

      const dayIndex = weekdayToIndex[dayOfWeek];
      const dayLabel =
        typeof dayIndex === "number"
          ? getWeekdayName(dayIndex, locale)
          : dayOfWeek
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()) || "-";

      const ranges = timeRanges
        .map((r) => (typeof r === "string" && r.length > 0 ? r : null))
        .filter((v): v is string => typeof v === "string")
        .join(", ");

      if (dayLabel === "-" || ranges.length === 0) return null;
      return `${dayLabel}: ${ranges}`;
    })
    .filter((v): v is string => typeof v === "string" && v.length > 0);

  return lines.length > 0 ? lines.join("\n") : "-";
};

const buildDiscountPeriodRows = (
  input: PayloadRecord,
  fieldLabels: Record<string, string> | undefined,
  locale?: string
): DetailRow[] => {
  const activePeriod = isRecord(input.activePeriod)
    ? (input.activePeriod as PayloadRecord)
    : undefined;
  const targetedPeriod = isRecord(input.targetedPeriod)
    ? (input.targetedPeriod as PayloadRecord)
    : undefined;

  const hasActiveRange =
    !!activePeriod &&
    typeof activePeriod.startDateTime === "string" &&
    typeof activePeriod.endDateTime === "string";

  const hasTargetedRange =
    !!targetedPeriod &&
    typeof targetedPeriod.startDateTime === "string" &&
    typeof targetedPeriod.endDateTime === "string";

  const offPeakHours = targetedPeriod?.offPeakHours;
  const hasOffPeakHours = isRecordArray(offPeakHours) && offPeakHours.length > 0;

  const rows: DetailRow[] = [];

  // If both ranges exist, treat activePeriod as visibility.
  // If only activePeriod exists, treat it as booking period (most common input from the agent).
  if (hasActiveRange) {
    rows.push({
      label: getFieldLabel(hasTargetedRange ? "visibilityPeriod" : "bookingPeriod", fieldLabels),
      value: toDisplayValue(activePeriod, "activePeriod"),
    });
  }

  if (hasTargetedRange) {
    rows.push({
      label: getFieldLabel("bookingPeriod", fieldLabels),
      value: toDisplayValue(
        { startDateTime: targetedPeriod?.startDateTime, endDateTime: targetedPeriod?.endDateTime },
        "targetedPeriod"
      ),
    });
  }

  if (hasOffPeakHours) {
    rows.push({
      label: getFieldLabel("weekdayCondition", fieldLabels),
      value: formatOffPeakHoursList(offPeakHours, locale),
    });
  }

  return rows;
};

const renderOffPeakHoursTable = (raw: unknown, locale?: string): ReactNode => {
  if (!isRecordArray(raw) || raw.length === 0) {
    return "-";
  }

  const rows = raw
    .map((item) => {
      const dayOfWeek = typeof item.dayOfWeek === "string" ? item.dayOfWeek : "";
      const timeRanges = Array.isArray(item.timeRanges) ? item.timeRanges : [];

      const dayIndex = weekdayToIndex[dayOfWeek];
      const dayLabel =
        typeof dayIndex === "number" ? getWeekdayName(dayIndex, locale) : dayOfWeek || "-";

      const ranges = timeRanges
        .map((r) => (typeof r === "string" && r.length > 0 ? r : toDisplayValue(r)))
        .filter((v) => v !== "-")
        .join(", ");

      return { dayLabel, ranges: ranges.length > 0 ? ranges : "-" };
    })
    .filter((r) => r.dayLabel !== "-");

  if (rows.length === 0) {
    return "-";
  }

  return (
    <div className="overflow-x-auto rounded-md border bg-white/80 dark:bg-black/30">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Day</th>
            <th className="min-w-32 px-3 py-2 text-left font-semibold">Time ranges</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dayLabel} className="border-b last:border-b-0">
              <td className="w-40 bg-muted/10 px-3 py-2 font-medium align-top">{r.dayLabel}</td>
              <td className="px-3 py-2 break-words">{r.ranges}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatTimeRange = (startTime: unknown, endTime: unknown): string => {
  if (typeof startTime !== "string" || typeof endTime !== "string") {
    return "-";
  }
  return `${startTime}–${endTime}`;
};

const createRowsFromKeys = (
  payload: PayloadRecord,
  keys: string[],
  fieldLabels?: Record<string, string>
): DetailRow[] =>
  keys
    .filter((key) => payload[key] !== undefined)
    .map((key) => ({
      label: getFieldLabel(key, fieldLabels),
      value: toDisplayValue(payload[key], key),
    }));

const DetailTable = ({
  rows,
  noDetailsProvided,
}: {
  rows: DetailRow[];
  noDetailsProvided: string;
}) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        {noDetailsProvided}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border bg-white/80 dark:bg-black/30">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b last:border-b-0">
              <td className="w-40 bg-muted/30 px-3 py-2 font-medium align-top">{row.label}</td>
              <td className="min-w-32 px-3 py-2 break-words whitespace-pre-wrap">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DiscountRulesValue = ({
  rawRules,
  currencySymbol,
}: {
  rawRules: unknown;
  currencySymbol: string;
}) => {
  const [expandedRuleIndices, setExpandedRuleIndices] = useState<Set<number>>(() => new Set());

  if (!Array.isArray(rawRules) || rawRules.length === 0) return <span>-</span>;

  const toggleRule = (index: number) => {
    setExpandedRuleIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="space-y-1">
      {rawRules.map((rule, index) => {
        if (!isRecord(rule)) {
          return (
            <div key={`rule-${index}`} className="break-words">
              {toDisplayValue(rule, "rules")}
            </div>
          );
        }

        const valueType = typeof rule.valueType === "string" ? rule.valueType : undefined;
        const value = rule.value;

        const valueText =
          valueType === "percentage" || valueType === "percent"
            ? `${toDisplayValue(value, "percentage")}% off`
            : `${toDisplayValue(value, "amount")}${currencySymbol} off`;

        const target = rule.target;
        if (!isRecord(target)) {
          return (
            <div key={`rule-${index}`} className="break-words">
              {valueText}
            </div>
          );
        }

        const targetType = typeof target.type === "string" ? target.type : undefined;
        const serviceIds = Array.isArray(target.serviceIds) ? target.serviceIds : undefined;

        if (targetType === "specific_services" && serviceIds) {
          const services = serviceIds
            .map((s) => (typeof s === "string" && s.length > 0 ? s : toDisplayValue(s)))
            .filter((s) => s !== "-");
          const serviceCount = services.length;
          const isExpanded = expandedRuleIndices.has(index);

          return (
            <div key={`rule-${index}`} className="break-words">
              <span>{valueText} </span>
              <span>
                --{serviceCount} {serviceCount === 1 ? "service" : "services"}
              </span>
              {serviceCount > 0 && (
                <button
                  type="button"
                  className="ml-2 text-xs font-medium underline underline-offset-2 text-primary"
                  onClick={() => toggleRule(index)}
                >
                  {isExpanded ? "Show less" : "Show all"}
                </button>
              )}
              {isExpanded && serviceCount > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">{services.join(", ")}</div>
              )}
            </div>
          );
        }

        if (targetType === "all_services") {
          return (
            <div key={`rule-${index}`} className="break-words">
              {valueText} — All services
            </div>
          );
        }

        if (targetType === "total") {
          return (
            <div key={`rule-${index}`} className="break-words">
              {valueText} — Total booking
            </div>
          );
        }

        return (
          <div key={`rule-${index}`} className="break-words">
            {valueText} — {toDisplayValue(target, "target")}
          </div>
        );
      })}
    </div>
  );
};

const formatDiscountRules = (rawRules: unknown, currencySymbol: string): ReactNode => (
  <DiscountRulesValue rawRules={rawRules} currencySymbol={currencySymbol} />
);

const OptionGroupsDisplay = ({
  optionGroups,
  currencySymbol,
}: {
  optionGroups: unknown;
  currencySymbol: string;
}) => {
  if (!Array.isArray(optionGroups) || optionGroups.length === 0) return null;

  return (
    <div className="space-y-2">
      {optionGroups.map((group, gIdx) => {
        if (!isRecord(group)) return null;
        const groupName = typeof group.name === "string" ? group.name : `Group ${gIdx + 1}`;
        const selectionType =
          group.selectionType === "MULTIPLE" ? "Multiple choice" : "Single choice";
        const options = Array.isArray(group.options) ? group.options : [];

        return (
          <div key={`og-${gIdx}`} className="rounded-md border">
            <div className="flex items-center gap-2 bg-muted/30 px-3 py-1.5">
              <span className="text-xs font-semibold">{groupName}</span>
              <span className="text-[10px] text-muted-foreground">({selectionType})</span>
            </div>
            {options.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/10">
                      <th className="px-3 py-1.5 text-left font-medium">Option</th>
                      <th className="min-w-32 px-3 py-1.5 text-right font-medium">Price</th>
                      {options.some(
                        (o: unknown) => isRecord(o) && (o.durationModifier as number) !== 0
                      ) && (
                        <th className="min-w-32 px-3 py-1.5 text-right font-medium">Duration</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {options.map((option: unknown, oIdx: number) => {
                      if (!isRecord(option)) return null;
                      const name = typeof option.optionName === "string" ? option.optionName : "-";
                      const priceMod =
                        typeof option.priceModifier === "number" ? option.priceModifier : 0;
                      const durMod =
                        typeof option.durationModifier === "number" ? option.durationModifier : 0;
                      const priceAmount = Math.abs(priceMod) / 100;
                      const priceText = stripTrailingZeros(priceAmount.toFixed(2));
                      const priceDisplay =
                        priceMod === 0
                          ? `0${currencySymbol}`
                          : priceMod > 0
                            ? `+${priceText}${currencySymbol}`
                            : `-${priceText}${currencySymbol}`;
                      const hasDuration = options.some(
                        (o: unknown) => isRecord(o) && (o.durationModifier as number) !== 0
                      );
                      const isDefault = option.isDefault === true;

                      return (
                        <tr key={`opt-${oIdx}`} className="border-b last:border-b-0">
                          <td className="px-3 py-1.5">
                            {name}
                            {isDefault && (
                              <span className="ml-1.5 rounded bg-primary/10 px-1 py-0.5 text-[10px] text-primary">
                                default
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{priceDisplay}</td>
                          {hasDuration && (
                            <td className="px-3 py-1.5 text-right font-mono">
                              {durMod === 0 ? "-" : durMod > 0 ? `+${durMod}min` : `${durMod}min`}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
    {children}
  </section>
);

const renderSortItemsTable = (payload: PayloadRecord, uiText: IntentDetailUiText) => {
  const rawItems = payload.items;
  if (!isRecordArray(rawItems)) {
    return <DetailTable rows={[]} noDetailsProvided={uiText.noDetailsProvided} />;
  }

  const sortedItems = [...rawItems].sort((a, b) => {
    const aOrder = typeof a.sortOrder === "number" ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const bOrder = typeof b.sortOrder === "number" ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  return (
    <div className="overflow-x-auto rounded-md border bg-white/80 dark:bg-black/30">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{uiText.table.newOrder}</th>
            <th className="min-w-32 px-3 py-2 text-left font-semibold">{uiText.table.itemId}</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, index) => (
            <tr key={`${String(item.id)}-${index}`} className="border-b last:border-b-0">
              <td className="px-3 py-2">
                {typeof item.sortOrder === "number" ? item.sortOrder : "-"}
              </td>
              <td className="px-3 py-2 break-words">{toDisplayValue(item.id)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderBatchUpdateServices = (
  payload: PayloadRecord,
  uiText: IntentDetailUiText,
  currencySymbol: string,
  fieldLabels?: Record<string, string>
) => {
  const updates = payload.updates;
  if (!isRecordArray(updates) || updates.length === 0) {
    return <DetailTable rows={[]} noDetailsProvided={uiText.noDetailsProvided} />;
  }

  return (
    <div className="space-y-3">
      {updates.map((update, index) => {
        const rawOptionGroups = update.optionGroups;
        const hasOptionGroups = Array.isArray(rawOptionGroups) && rawOptionGroups.length > 0;

        const detailRows = Object.entries(update)
          .filter(
            ([key, value]) => key !== "serviceId" && key !== "optionGroups" && value !== undefined
          )
          .map(([key, value]) => ({
            label: getFieldLabel(key, fieldLabels),
            value: toDisplayValue(value, key),
          }));

        return (
          <div key={`${String(update.serviceId)}-${index}`} className="space-y-2">
            <div className="text-xs font-semibold">
              {uiText.labels.serviceItemTitle(index + 1)}: {toDisplayValue(update.serviceId)}
            </div>
            <DetailTable rows={detailRows} noDetailsProvided={uiText.noDetailsProvided} />
            {hasOptionGroups && (
              <OptionGroupsDisplay optionGroups={rawOptionGroups} currencySymbol={currencySymbol} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const formatPriceModifier = (priceMod: number, currencySymbol: string): string => {
  if (priceMod === 0) return `0${currencySymbol}`;
  const priceText = stripTrailingZeros((Math.abs(priceMod) / 100).toFixed(2));
  return priceMod > 0 ? `+${priceText}${currencySymbol}` : `-${priceText}${currencySymbol}`;
};

const formatDurationModifier = (durMod: number): string => {
  if (durMod === 0) return "—";
  return durMod > 0 ? `+${durMod}min` : `${durMod}min`;
};

const BatchCreateServicesView = ({
  services,
  uiText,
  currencySymbol,
}: {
  services: PayloadRecord[];
  uiText: IntentDetailUiText;
  currencySymbol: string;
}) => {
  const showCode = hasField(services, "serviceCode");
  const showCategory = hasField(services, "categoryId");
  const showPrice = hasField(services, "basePrice");
  const showDuration = hasField(services, "duration");

  const columnCount =
    2 + (showCode ? 1 : 0) + (showCategory ? 1 : 0) + (showPrice ? 1 : 0) + (showDuration ? 1 : 0);

  return (
    <div className="overflow-x-auto rounded-md border bg-white/80 dark:bg-black/30">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{uiText.table.index}</th>
            <th className="min-w-24 px-3 py-2 text-left font-semibold">{uiText.table.name}</th>
            {showCode && (
              <th className="min-w-20 px-3 py-2 text-left font-semibold">
                {uiText.table.serviceCode}
              </th>
            )}
            {showCategory && (
              <th className="min-w-20 px-3 py-2 text-left font-semibold">
                {uiText.table.categoryId}
              </th>
            )}
            {showPrice && (
              <th className="min-w-20 px-3 py-2 text-right font-semibold">
                {uiText.table.basePrice}
              </th>
            )}
            {showDuration && (
              <th className="min-w-20 px-3 py-2 text-right font-semibold">
                {uiText.table.duration}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {services.map((service, index) => {
            const optionGroups = Array.isArray(service.optionGroups) ? service.optionGroups : [];
            return (
              <Fragment key={`${String(service.name)}-${index}`}>
                <tr className="border-b last:border-b-0">
                  <td className="px-3 py-2 align-top">{index + 1}</td>
                  <td className="px-3 py-2 align-top break-words font-medium">
                    {toDisplayValue(service.name)}
                  </td>
                  {showCode && (
                    <td className="px-3 py-2 align-top break-words">
                      {toDisplayValue(service.serviceCode)}
                    </td>
                  )}
                  {showCategory && (
                    <td className="px-3 py-2 align-top break-words">
                      {toDisplayValue(service.categoryId)}
                    </td>
                  )}
                  {showPrice && (
                    <td className="px-3 py-2 align-top text-right font-mono">
                      {toDisplayValue(service.basePrice, "basePrice")}
                    </td>
                  )}
                  {showDuration && (
                    <td className="px-3 py-2 align-top text-right font-mono">
                      {toDisplayValue(service.duration)}
                    </td>
                  )}
                </tr>
                {optionGroups.flatMap((group, gIdx) => {
                  if (!isRecord(group)) return [];
                  const groupName =
                    typeof group.name === "string" ? group.name : `Group ${gIdx + 1}`;
                  const selectionType =
                    group.selectionType === "MULTIPLE" ? "Multiple choice" : "Single choice";
                  const options = Array.isArray(group.options) ? group.options : [];
                  const groupHasDuration = options.some(
                    (opt) =>
                      isRecord(opt) &&
                      typeof opt.durationModifier === "number" &&
                      opt.durationModifier !== 0
                  );

                  return [
                    <tr
                      key={`${index}-g-${gIdx}-header`}
                      className="border-b last:border-b-0 bg-muted/5"
                    >
                      <td className="px-3 py-1.5 align-top" />
                      <td colSpan={columnCount - 1} className="px-3 py-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {groupName}
                        </span>
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          ({selectionType})
                        </span>
                      </td>
                    </tr>,
                    <tr
                      key={`${index}-g-${gIdx}-options`}
                      className="border-b last:border-b-0 bg-muted/5 text-muted-foreground"
                    >
                      <td className="px-3 py-1.5 align-top" />
                      <td colSpan={columnCount - 1} className="px-3 py-1.5 align-top">
                        <div
                          className={`grid w-fit gap-x-10 gap-y-2 ${
                            groupHasDuration
                              ? "grid-cols-[auto_auto_auto]"
                              : "grid-cols-[auto_auto]"
                          }`}
                        >
                          {options.map((option, oIdx) => {
                            if (!isRecord(option)) return null;
                            const name =
                              typeof option.optionName === "string" ? option.optionName : "-";
                            const priceMod =
                              typeof option.priceModifier === "number" ? option.priceModifier : 0;
                            const durMod =
                              typeof option.durationModifier === "number"
                                ? option.durationModifier
                                : 0;

                            return (
                              <Fragment key={`${index}-g-${gIdx}-o-${oIdx}`}>
                                <span className="break-words">{name}</span>
                                <span className="font-mono">
                                  {formatPriceModifier(priceMod, currencySymbol)}
                                </span>
                                {groupHasDuration && (
                                  <span className="font-mono">
                                    {durMod === 0 ? "—" : formatDurationModifier(durMod)}
                                  </span>
                                )}
                              </Fragment>
                            );
                          })}
                        </div>
                      </td>
                    </tr>,
                  ];
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const renderBatchCreateServices = (
  payload: PayloadRecord,
  uiText: IntentDetailUiText,
  currencySymbol: string
) => {
  const services = payload.services;
  if (!isRecordArray(services) || services.length === 0) {
    return <DetailTable rows={[]} noDetailsProvided={uiText.noDetailsProvided} />;
  }
  return (
    <BatchCreateServicesView services={services} uiText={uiText} currencySymbol={currencySymbol} />
  );
};

const hasField = (items: PayloadRecord[], key: string): boolean =>
  items.some((item) => item[key] !== undefined && item[key] !== null);

const renderBatchUpdateCategories = (
  payload: PayloadRecord,
  uiText: IntentDetailUiText,
  fieldLabels?: Record<string, string>
) => {
  const updates = payload.updates;
  if (!isRecordArray(updates) || updates.length === 0) {
    return <DetailTable rows={[]} noDetailsProvided={uiText.noDetailsProvided} />;
  }

  return (
    <div className="space-y-3">
      {updates.map((update, index) => {
        const detailRows = Object.entries(update)
          .filter(([key, value]) => key !== "categoryId" && value !== undefined)
          .map(([key, value]) => ({
            label: getFieldLabel(key, fieldLabels),
            value: toDisplayValue(value, key),
          }));

        return (
          <div key={`${String(update.categoryId)}-${index}`} className="space-y-2">
            <div className="text-xs font-semibold">
              {`${index + 1}. ${toDisplayValue(update.categoryId)}`}
            </div>
            <DetailTable rows={detailRows} noDetailsProvided={uiText.noDetailsProvided} />
          </div>
        );
      })}
    </div>
  );
};

const renderBatchCreateCategories = (payload: PayloadRecord, uiText: IntentDetailUiText) => {
  const categories = payload.categories;
  if (!isRecordArray(categories) || categories.length === 0) {
    return <DetailTable rows={[]} noDetailsProvided={uiText.noDetailsProvided} />;
  }

  const showDesc = hasField(categories, "description");
  const showColor = hasField(categories, "color");
  const showBooking = hasField(categories, "allowOnlineBooking");

  return (
    <div className="overflow-x-auto rounded-md border bg-white/80 dark:bg-black/30">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{uiText.table.index}</th>
            <th className="min-w-32 px-3 py-2 text-left font-semibold">{uiText.table.name}</th>
            {showDesc && (
              <th className="min-w-32 px-3 py-2 text-left font-semibold">
                {uiText.table.description}
              </th>
            )}
            {showColor && (
              <th className="min-w-32 px-3 py-2 text-left font-semibold">{uiText.table.color}</th>
            )}
            {showBooking && (
              <th className="min-w-32 px-3 py-2 text-left font-semibold">
                {uiText.table.onlineBooking}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {categories.map((category, index) => (
            <tr key={`${String(category.name)}-${index}`} className="border-b last:border-b-0">
              <td className="px-3 py-2">{index + 1}</td>
              <td className="px-3 py-2 break-words">{toDisplayValue(category.name)}</td>
              {showDesc && (
                <td className="px-3 py-2 break-words">{toDisplayValue(category.description)}</td>
              )}
              {showColor && <td className="px-3 py-2">{toDisplayValue(category.color)}</td>}
              {showBooking && (
                <td className="px-3 py-2">{toDisplayValue(category.allowOnlineBooking)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderCreateAppointment = (
  payload: PayloadRecord,
  fieldLabels: Record<string, string> | undefined,
  uiText: IntentDetailUiText
) => {
  const rows: DetailRow[] = [];

  if (payload.customerId !== undefined) {
    rows.push({
      label: getFieldLabel("customerId", fieldLabels),
      value: toDisplayValue(payload.customerId, "customerId"),
    });
  }

  if (payload.startTime !== undefined) {
    rows.push({
      label: getFieldLabel("startTime", fieldLabels),
      value: toDisplayValue(payload.startTime, "startTime"),
    });
  }

  const rawSelections = payload.serviceSelections;
  if (isRecordArray(rawSelections) && rawSelections.length > 0) {
    const services = rawSelections
      .map((selection) => selection.serviceId)
      .map((serviceId) =>
        typeof serviceId === "string" && serviceId.length > 0
          ? serviceId
          : toDisplayValue(serviceId, "serviceId")
      )
      .filter((value) => value !== "-");

    rows.push({
      label: getFieldLabel("serviceSelections", fieldLabels),
      value: services.length > 0 ? services.join(", ") : "-",
    });
  }

  // Optional free-text fields (omit customerLanguage by design).
  rows.push(
    ...createRowsFromKeys(payload, ["notes", "internalNotes", "customerMessage"], fieldLabels)
  );

  return (
    <Section title={uiText.sections.payload}>
      <DetailTable rows={rows} noDetailsProvided={uiText.noDetailsProvided} />
    </Section>
  );
};

export const renderIntentDetails = (
  actionType: IntentActionType,
  payload: PayloadRecord,
  fieldLabels?: Record<string, string>,
  locale?: string,
  currencySymbol: string = "$",
  uiText?: IntentDetailUiText
): ReactNode => {
  const resolvedUiText: IntentDetailUiText =
    uiText ??
    ({
      noDetailsProvided: "No details provided.",
      sections: {
        target: "Target",
        fieldsToApply: "Fields To Apply",
        payload: "Payload",
        sortTarget: "Sort Target",
        newOrder: "New Order",
        serviceUpdates: "Service Updates",
        targetScope: "Target Scope",
        bulkChanges: "Bulk Changes",
        copyScope: "Copy Scope",
        copyModifiers: "Copy Modifiers",
        restrictionPlan: "Restriction Plan",
        newServices: "New Services",
        newCategories: "New Categories",
        operatingHours: "Operating Hours",
        assignment: "Assignment",
      },
      table: {
        newOrder: "New Order",
        itemId: "Item ID",
        index: "#",
        name: "Name",
        serviceCode: "Service Code",
        categoryId: "Category ID",
        basePrice: "Base Price",
        duration: "Duration",
        description: "Description",
        color: "Color",
        onlineBooking: "Online Booking",
      },
      labels: {
        serviceIds: "Service IDs",
        categoryIds: "Category IDs",
        closed: "Closed",
        unassigned: "Unassigned",
        discountApplyToTotal: "Total",
        discountApplyToPerItem: "Per item",
        serviceItemTitle: (index: number) => `Service ${index}`,
        serviceFallback: "Service",
        workerFallback: "Worker",
      },
    } satisfies IntentDetailUiText);

  switch (actionType) {
    case "CREATE_DISCOUNT": {
      const input = isRecord(payload.input) ? (payload.input as PayloadRecord) : undefined;
      const rows = input
        ? ((): DetailRow[] => {
            const base: DetailRow[] = [];

            if (input.title !== undefined) {
              base.push({
                label: getFieldLabel("title", fieldLabels),
                value: toDisplayValue(input.title, "title"),
              });
            }

            if (input.applyTo !== undefined) {
              const offPeakHours =
                isRecord(input.applyTo) && input.applyTo.offPeakHours !== undefined
                  ? input.applyTo.offPeakHours
                  : undefined;

              base.push({
                label: getFieldLabel("applyTo", fieldLabels),
                value:
                  offPeakHours !== undefined
                    ? renderOffPeakHoursTable(offPeakHours, locale)
                    : formatDiscountApplyTo(input.applyTo, resolvedUiText),
              });
            }

            if (input.rules !== undefined) {
              base.push({
                label: getFieldLabel("rules", fieldLabels),
                value: formatDiscountRules(input.rules, currencySymbol),
              });
            }

            if (input.minSpent !== undefined) {
              base.push({
                label: getFieldLabel("minSpent", fieldLabels),
                value: formatCentsAsCurrency(input.minSpent, currencySymbol),
              });
            }

            base.push(...buildDiscountPeriodRows(input, fieldLabels, locale));

            return base;
          })()
        : createRowsFromKeys(payload, ["input"], fieldLabels);

      return (
        <Section title={uiText?.sections.payload ?? resolvedUiText.sections.payload}>
          <DetailTable rows={rows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
        </Section>
      );
    }

    case "UPDATE_DISCOUNT": {
      const input = isRecord(payload.input) ? (payload.input as PayloadRecord) : undefined;
      const targetKeys: string[] =
        typeof payload.discountTitle === "string" && payload.discountTitle.trim().length > 0
          ? ["discountTitle"]
          : [];

      const targetRows = createRowsFromKeys(payload, targetKeys, fieldLabels);
      const rows = input
        ? ((): DetailRow[] => {
            const base: DetailRow[] = [];

            if (input.title !== undefined) {
              base.push({
                label: getFieldLabel("title", fieldLabels),
                value: toDisplayValue(input.title, "title"),
              });
            }

            if (input.applyTo !== undefined) {
              const offPeakHours =
                isRecord(input.applyTo) && input.applyTo.offPeakHours !== undefined
                  ? input.applyTo.offPeakHours
                  : undefined;

              base.push({
                label: getFieldLabel("applyTo", fieldLabels),
                value:
                  offPeakHours !== undefined
                    ? renderOffPeakHoursTable(offPeakHours, locale)
                    : formatDiscountApplyTo(input.applyTo, resolvedUiText),
              });
            }

            if (input.rules !== undefined) {
              base.push({
                label: getFieldLabel("rules", fieldLabels),
                value: formatDiscountRules(input.rules, currencySymbol),
              });
            }

            if (input.minSpent !== undefined) {
              base.push({
                label: getFieldLabel("minSpent", fieldLabels),
                value: formatCentsAsCurrency(input.minSpent, currencySymbol),
              });
            }

            base.push(...buildDiscountPeriodRows(input, fieldLabels, locale));

            if (input.isActive !== undefined) {
              base.push({
                label: getFieldLabel("isActive", fieldLabels),
                value: toDisplayValue(input.isActive, "isActive"),
              });
            }

            return base;
          })()
        : createRowsFromKeys(payload, ["input"], fieldLabels);

      return (
        <div className="space-y-3">
          {targetRows.length > 0 && (
            <Section title={resolvedUiText.sections.target}>
              <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
            </Section>
          )}
          <Section title={resolvedUiText.sections.fieldsToApply}>
            <DetailTable rows={rows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
        </div>
      );
    }

    case "DELETE_DISCOUNT": {
      const targetKeys: string[] =
        typeof payload.discountTitle === "string" && payload.discountTitle.trim().length > 0
          ? ["discountTitle"]
          : [];
      const rows = createRowsFromKeys(payload, targetKeys, fieldLabels);
      return (
        <Section title={resolvedUiText.sections.target}>
          <DetailTable rows={rows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
        </Section>
      );
    }

    case "MANAGE_SERVICE": {
      const targetRows = createRowsFromKeys(payload, ["serviceId"], fieldLabels);
      const changedRows = createRowsFromKeys(
        payload,
        [
          "name",
          "serviceCode",
          "categoryId",
          "pricingMode",
          "basePrice",
          "duration",
          "description",
          "allowOnlineBooking",
          "isActive",
        ],
        fieldLabels
      );
      const rawOptionGroups = payload.optionGroups;
      const hasOptionGroups = Array.isArray(rawOptionGroups) && rawOptionGroups.length > 0;

      return (
        <div className="space-y-3">
          {targetRows.length > 0 && (
            <Section title={resolvedUiText.sections.target}>
              <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
            </Section>
          )}
          <Section title={resolvedUiText.sections.fieldsToApply}>
            <DetailTable rows={changedRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
          {hasOptionGroups && (
            <Section title={resolvedUiText.sections.optionGroups ?? "Option Groups"}>
              <OptionGroupsDisplay optionGroups={rawOptionGroups} currencySymbol={currencySymbol} />
            </Section>
          )}
        </div>
      );
    }
    case "MANAGE_CATEGORY": {
      const targetRows = createRowsFromKeys(payload, ["categoryId"], fieldLabels);
      const changedRows = createRowsFromKeys(
        payload,
        ["name", "description", "color", "icon", "allowOnlineBooking", "isArchived"],
        fieldLabels
      );

      return (
        <div className="space-y-3">
          {targetRows.length > 0 && (
            <Section title={resolvedUiText.sections.target}>
              <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
            </Section>
          )}
          <Section title={resolvedUiText.sections.fieldsToApply}>
            <DetailTable rows={changedRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
        </div>
      );
    }
    case "SORT_ITEMS": {
      const targetRows = createRowsFromKeys(payload, ["type"], fieldLabels);

      return (
        <div className="space-y-3">
          <Section title={resolvedUiText.sections.sortTarget}>
            <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
          <Section title={resolvedUiText.sections.newOrder}>
            {renderSortItemsTable(payload, resolvedUiText)}
          </Section>
        </div>
      );
    }
    case "BATCH_UPDATE_SERVICES":
      return (
        <Section title={resolvedUiText.sections.serviceUpdates}>
          {renderBatchUpdateServices(payload, resolvedUiText, currencySymbol, fieldLabels)}
        </Section>
      );
    case "BULK_UPDATE_SERVICES": {
      const targetRows = createRowsFromKeys(payload, ["categoryId"], fieldLabels);
      if (isStringArray(payload.serviceIds)) {
        targetRows.unshift({
          label: resolvedUiText.labels.serviceIds,
          value: payload.serviceIds.join(", ") || "-",
        });
      }

      const operationRows: DetailRow[] = [];

      const rawPriceAdjustment = safeParseJson(payload.priceAdjustment);
      if (rawPriceAdjustment !== undefined) {
        operationRows.push({
          label: getFieldLabel("priceAdjustment", fieldLabels),
          value: formatPriceAdjustmentValue(rawPriceAdjustment, currencySymbol),
        });
      }

      operationRows.push(
        ...createRowsFromKeys(
          payload,
          ["durationAdjustment", "categoryChange", "archive", "allowOnlineBooking"],
          fieldLabels
        )
      );

      return (
        <div className="space-y-3">
          <Section title={resolvedUiText.sections.targetScope}>
            <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
          <Section title={resolvedUiText.sections.bulkChanges}>
            <DetailTable
              rows={operationRows}
              noDetailsProvided={resolvedUiText.noDetailsProvided}
            />
          </Section>
        </div>
      );
    }
    case "COPY_SERVICES": {
      const targetRows = createRowsFromKeys(payload, ["targetCategoryId"], fieldLabels);
      const operationRows = createRowsFromKeys(
        payload,
        ["priceAdjustment", "namePrefix", "nameSuffix"],
        fieldLabels
      );
      if (isStringArray(payload.serviceIds)) {
        targetRows.unshift({
          label: resolvedUiText.labels.serviceIds,
          value: payload.serviceIds.join(", ") || "-",
        });
      }

      return (
        <div className="space-y-3">
          <Section title={resolvedUiText.sections.copyScope}>
            <DetailTable rows={targetRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
          <Section title={resolvedUiText.sections.copyModifiers}>
            <DetailTable
              rows={operationRows}
              noDetailsProvided={resolvedUiText.noDetailsProvided}
            />
          </Section>
        </div>
      );
    }
    case "UPDATE_WORKER_SERVICES":
    case "UPDATE_WORKER_SERVICE_RESTRICTIONS": {
      const coreRows = createRowsFromKeys(
        payload,
        ["workerId", "workerSalonId", "restrictionMode"],
        fieldLabels
      );
      if (isStringArray(payload.serviceIds)) {
        coreRows.push({
          label: resolvedUiText.labels.serviceIds,
          value: payload.serviceIds.join(", ") || "-",
        });
      }
      if (isStringArray(payload.categoryIds)) {
        coreRows.push({
          label: resolvedUiText.labels.categoryIds,
          value: payload.categoryIds.join(", ") || "-",
        });
      }

      return (
        <Section title={resolvedUiText.sections.restrictionPlan}>
          <DetailTable rows={coreRows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
        </Section>
      );
    }
    case "BATCH_CREATE_SERVICES":
      return (
        <Section title={resolvedUiText.sections.newServices}>
          {renderBatchCreateServices(payload, resolvedUiText, currencySymbol)}
        </Section>
      );
    case "BATCH_CREATE_CATEGORIES":
      return (
        <Section title={resolvedUiText.sections.newCategories}>
          {renderBatchCreateCategories(payload, resolvedUiText)}
        </Section>
      );
    case "BATCH_UPDATE_CATEGORIES":
      return (
        <Section title={resolvedUiText.sections.fieldsToApply}>
          {renderBatchUpdateCategories(payload, resolvedUiText, fieldLabels)}
        </Section>
      );
    case "UPDATE_OPERATING_HOURS": {
      const operatingHours = payload.operatingHours;
      if (!isRecordArray(operatingHours) || operatingHours.length === 0) {
        return (
          <Section title={resolvedUiText.sections.operatingHours}>
            <DetailTable rows={[]} noDetailsProvided={resolvedUiText.noDetailsProvided} />
          </Section>
        );
      }

      const rows: DetailRow[] = operatingHours
        .filter((day) => typeof day.dayOfWeek === "number")
        .sort((a, b) => (a.dayOfWeek as number) - (b.dayOfWeek as number))
        .map((day) => {
          const dayLabel = getWeekdayName(day.dayOfWeek as number, locale);
          const timeSlots = Array.isArray(day.timeSlots) ? day.timeSlots : [];

          if (timeSlots.length === 0) {
            return { label: dayLabel, value: resolvedUiText.labels.closed };
          }

          const ranges = timeSlots
            .slice()
            .sort((a, b) => {
              const aOrder =
                typeof a?.slotOrder === "number" ? a.slotOrder : Number.MAX_SAFE_INTEGER;
              const bOrder =
                typeof b?.slotOrder === "number" ? b.slotOrder : Number.MAX_SAFE_INTEGER;
              return aOrder - bOrder;
            })
            .map((slot) => formatTimeRange(slot?.startTime, slot?.endTime))
            .filter((value) => value !== "-");

          return {
            label: dayLabel,
            value: ranges.length > 0 ? ranges.join(", ") : "-",
          };
        });

      return (
        <Section title={resolvedUiText.sections.operatingHours}>
          <DetailTable rows={rows} noDetailsProvided={resolvedUiText.noDetailsProvided} />
        </Section>
      );
    }
    case "CREATE_APPOINTMENT":
      return renderCreateAppointment(payload, fieldLabels, resolvedUiText);
    case "ASSIGN_APPOINTMENT_WORKER": {
      const serviceLabel =
        fieldLabels?.appointmentServiceId ?? resolvedUiText.labels.serviceFallback;
      const workerLabel = fieldLabels?.workerId ?? resolvedUiText.labels.workerFallback;

      const serviceValue =
        typeof payload.appointmentServiceId === "string"
          ? payload.appointmentServiceId
          : toDisplayValue(payload.appointmentServiceId);

      const workerValue =
        payload.workerId === null
          ? resolvedUiText.labels.unassigned
          : typeof payload.workerId === "string"
            ? payload.workerId
            : toDisplayValue(payload.workerId);

      return (
        <Section title={resolvedUiText.sections.assignment}>
          <DetailTable
            rows={[
              { label: serviceLabel, value: serviceValue },
              { label: workerLabel, value: workerValue },
            ]}
            noDetailsProvided={resolvedUiText.noDetailsProvided}
          />
        </Section>
      );
    }
    default:
      return (
        <Section title={resolvedUiText.sections.payload}>
          <DetailTable
            rows={Object.entries(payload)
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => ({
                label: getFieldLabel(key, fieldLabels),
                value: toDisplayValue(value, key),
              }))}
            noDetailsProvided={resolvedUiText.noDetailsProvided}
          />
        </Section>
      );
  }
};
